import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Ban,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  Clock3,
  Copy,
  FileCheck2,
  FileText,
  Globe2,
  HelpCircle,
  ListChecks,
  LoaderCircle,
  LockKeyhole,
  MessageCircleQuestion,
  MessageSquareWarning,
  Phone,
  ReceiptText,
  RefreshCw,
  SearchCheck,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Trash2,
  UploadCloud,
  UserRoundCheck,
  WalletCards,
  X,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConfirmDialog, useToast } from "@tribunalea/ui";
import { CivitaAssistantLauncher } from "../components/CivitaAssistant.jsx";
import { DocumentPreview } from "../components/DocumentPreview.jsx";

const categoryOptions = Object.freeze([
  { id: "message", label: "Mensaje o llamada", description: "WhatsApp, SMS, correo o teléfono", icon: MessageCircleQuestion },
  { id: "payment_request", label: "Pedido de pago", description: "Transferencia, factura o deuda", icon: WalletCards },
  { id: "website", label: "Sitio o enlace", description: "Página, formulario o acceso", icon: Globe2 },
  { id: "offer", label: "Oferta o compra", description: "Premio, venta, trabajo o inversión", icon: ReceiptText },
  { id: "document", label: "Documento o aviso", description: "Comprobante, carta o notificación", icon: FileText },
  { id: "identity", label: "Persona o empresa", description: "Alguien que dice representar a otra parte", icon: UserRoundCheck },
  { id: "other", label: "Otra situación", description: "Cualquier pedido que te genere dudas", icon: CircleHelp },
]);

const channelOptions = Object.freeze([
  ["whatsapp", "WhatsApp"],
  ["email", "Correo electrónico"],
  ["sms", "Mensaje de texto (SMS)"],
  ["social", "Red social"],
  ["phone", "Llamada telefónica"],
  ["website", "Sitio web"],
  ["in_person", "En persona"],
  ["other", "Otro medio"],
]);

const actionOptions = Object.freeze([
  ["click_link", "Abrir un enlace o escanear un código"],
  ["payment", "Pagar o transferir dinero"],
  ["personal_data", "Compartir datos personales o bancarios"],
  ["login", "Ingresar una contraseña o código"],
  ["download", "Descargar o instalar algo"],
  ["sign", "Firmar o aceptar una condición"],
  ["reply", "Responder o comunicarse"],
  ["other", "Otra acción"],
]);

const actedOptions = Object.freeze([
  ["no_action", "No hice nada todavía"],
  ["opened_or_replied", "Abrí el enlace o respondí"],
  ["shared_sensitive", "Compartí datos, una clave o un código"],
  ["paid", "Pagué o transferí dinero"],
  ["installed", "Descargué o instalé algo"],
  ["other", "Hice otra cosa"],
]);

const pressureOptions = Object.freeze([
  { id: "none", label: "Sin apuro", description: "No fijaron un plazo" },
  { id: "soon", label: "Pronto", description: "Insisten o vence en estos días" },
  { id: "immediate", label: "Ahora", description: "Amenazan con una consecuencia inmediata" },
]);

const verdicts = Object.freeze({
  high_risk: { label: "Riesgo alto", short: "No avances por ahora", icon: ShieldAlert },
  suspicious: { label: "Hay señales de alerta", short: "Verificá antes de actuar", icon: MessageSquareWarning },
  needs_verification: { label: "Necesita verificación", short: "Todavía no está confirmado", icon: SearchCheck },
  no_clear_warning: { label: "No vemos alertas claras", short: "Eso no confirma que sea legítimo", icon: ShieldCheck },
  insufficient_information: { label: "Falta información", short: "No alcanza para evaluarlo", icon: HelpCircle },
});

const priorityLabels = Object.freeze({
  now: "Hacé esto ahora",
  before_acting: "Antes de avanzar",
  optional: "Para confirmar mejor",
});

const legitimacyDemoLines = [
  "BANCO DEL CENTRO",
  "ALERTA DE SEGURIDAD",
  "",
  "Tu cuenta será bloqueada hoy por actividad inusual.",
  "Validá tu identidad de inmediato ingresando en:",
  "bancodelcentro-seguridad.example/validar",
  "",
  "Tenés 30 minutos para evitar el bloqueo.",
  "Nunca compartas este mensaje.",
];

const demoCheck = Object.freeze({
  id: "verificacion-ejemplo",
  notebookId: "ejemplo",
  category: "message",
  description: "Recibí un SMS que dice que mi cuenta bancaria será bloqueada y me pide validar mis datos desde un enlace.",
  claimedSender: "Banco del Centro",
  receivedThrough: "sms",
  requestedAction: "login",
  detailsToCheck: "bancodelcentro-seguridad.example/validar",
  concern: "Nunca recibí un aviso así y me da miedo perder el acceso a la cuenta.",
  pressure: "immediate",
  alreadyActed: "no_action",
  demo: true,
  sources: [{ id: "captura", fileName: "captura-del-mensaje.pdf", status: "ready", pageCount: 1 }],
  analysisStatus: "ready",
  status: "ready",
  createdAt: new Date().toISOString(),
  assessment: {
    title: "Mensaje bancario con presión y pedido de acceso",
    verdict: "suspicious",
    confidence: "high",
    confidenceReason: "La captura permite observar varias señales concretas, pero no se consultó al banco ni ninguna fuente externa.",
    summary: "El mensaje usa una consecuencia urgente para llevarte a un enlace y pedirte una validación de identidad. Esas señales justifican frenar y comprobar el aviso desde un canal independiente.",
    decisionMessage: "No uses el enlace del mensaje. Comprobá primero si existe una alerta real desde la aplicación que ya usás o un contacto oficial obtenido por separado.",
    immediateAction: {
      title: "No abras el enlace ni ingreses datos",
      explanation: "El pedido todavía no está confirmado y podés verificarlo sin usar ningún dato incluido en el SMS.",
    },
    observedFacts: [
      { fact: "El mensaje anuncia que la cuenta será bloqueada hoy.", origin: "attachment", citations: [{ fileName: "captura-del-mensaje.pdf", page: 1, quote: "Tu cuenta será bloqueada hoy por actividad inusual." }] },
      { fact: "Pide validar la identidad mediante un enlace incluido en el mismo mensaje.", origin: "attachment", citations: [{ fileName: "captura-del-mensaje.pdf", page: 1, quote: "Validá tu identidad de inmediato ingresando en:" }] },
      { fact: "La persona todavía no abrió el enlace ni compartió información.", origin: "user_report", citations: [] },
    ],
    warningSigns: [
      { severity: "high", title: "Presión de tiempo", explanation: "Fija treinta minutos para actuar y amenaza con un bloqueo. La urgencia puede impedir que compruebes el origen con calma.", citations: [{ fileName: "captura-del-mensaje.pdf", page: 1, quote: "Tenés 30 minutos para evitar el bloqueo." }] },
      { severity: "high", title: "Pide ingresar desde el enlace recibido", explanation: "La supuesta solución depende de usar una dirección enviada por el mismo remitente cuya identidad todavía no está verificada.", citations: [{ fileName: "captura-del-mensaje.pdf", page: 1, quote: "bancodelcentro-seguridad.example/validar" }] },
      { severity: "medium", title: "Intenta desalentar que lo compartas", explanation: "La frase puede reducir la posibilidad de pedir una segunda opinión antes de actuar.", citations: [{ fileName: "captura-del-mensaje.pdf", page: 1, quote: "Nunca compartas este mensaje." }] },
    ],
    reassuringSigns: [],
    unverifiedClaims: [
      { claim: "El mensaje fue enviado por Banco del Centro.", whyUnverified: "El nombre visible y el logo de una captura no demuestran quién originó el SMS.", howToVerify: "Consultá la aplicación oficial o llamá a un número obtenido de tu tarjeta o del sitio que ya conocías, no del mensaje." },
      { claim: "Existe actividad inusual y la cuenta será bloqueada.", whyUnverified: "La captura contiene la afirmación, pero no una confirmación independiente de la cuenta.", howToVerify: "Revisá las notificaciones dentro de la aplicación habitual o consultá al banco desde un canal independiente." },
    ],
    verificationSteps: [
      { priority: "now", title: "Entrá por tu aplicación habitual", instruction: "Abrí la aplicación bancaria desde el ícono que ya tenés instalado. No copies ni abras la dirección del SMS.", reason: "Permite revisar alertas sin depender del canal dudoso." },
      { priority: "before_acting", title: "Consultá por un contacto independiente", instruction: "Si necesitás llamar, usá el teléfono que figura en tu tarjeta o en un sitio al que ingreses escribiendo vos la dirección conocida.", reason: "El supuesto remitente no controla el canal que usás para comprobarlo." },
      { priority: "optional", title: "Conservá la captura", instruction: "Guardala junto con la fecha, hora y número remitente sin responderle.", reason: "Puede servir para explicar el caso al banco o hacer un reporte si fuera necesario." },
    ],
    avoidActions: [
      "No abras el enlace ni escanees códigos incluidos en el mensaje.",
      "No compartas contraseñas, códigos de seguridad ni números completos de tarjeta.",
      "No llames a un número que aparezca sólo en esta comunicación.",
    ],
    recoveryPlan: {
      needed: false,
      title: "Todavía estás a tiempo de verificar sin exponerte",
      steps: [],
    },
    questionsForOfficialChannel: [
      "¿Existe una alerta real de seguridad o bloqueo sobre mi cuenta?",
      "¿El banco envió este mensaje y reconoce esa dirección?",
      "¿Conviene reportar el número remitente y la captura?",
    ],
  },
});

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusStep({ state, children }) {
  return (
    <li className={`processing-step is-${state}`}>
      <span>{state === "done" ? <Check size={15} /> : state === "active" ? <LoaderCircle className="is-spinning" size={15} /> : null}</span>
      {children}
    </li>
  );
}

function LegitimacyProcessing({ check, onBack }) {
  const extracted = check.sources?.every(({ status }) => status === "ready");
  return (
    <main className="civita-document-page legitimacy-page processing-view">
      <button className="text-button" type="button" onClick={onBack}><ArrowLeft size={16} /> Volver</button>
      <section className="processing-card" aria-live="polite">
        <span className="processing-card__icon"><SearchCheck size={25} /></span>
        <span className="eyebrow">Verificar si es legítimo</span>
        <h1>Estamos separando hechos de señales</h1>
        <p>Revisamos lo que contaste y los archivos sin abrir enlaces ni contactar al remitente.</p>
        <ol>
          <StatusStep state="done">Situación recibida de forma segura</StatusStep>
          <StatusStep state={extracted ? "done" : "active"}>Leyendo capturas y documentos</StatusStep>
          <StatusStep state={extracted ? "active" : "pending"}>Ordenando riesgos y formas independientes de comprobarlos</StatusStep>
        </ol>
        <small>Al terminar, la pantalla quedará arriba del resultado.</small>
      </section>
    </main>
  );
}

function LegitimacyError({ check, retrying, onRetry, onBack }) {
  return (
    <main className="civita-document-page legitimacy-page processing-view">
      <section className="processing-card is-error">
        <span className="processing-card__icon"><AlertCircle size={25} /></span>
        <span className="eyebrow">No pudimos terminar</span>
        <h1>Necesitamos intentar otra vez</h1>
        <p>{check.error?.message || "Ocurrió un problema al revisar la información."}</p>
        <div className="button-row">
          <button className="primary-button" type="button" onClick={onRetry} disabled={retrying}>{retrying ? <LoaderCircle className="is-spinning" size={17} /> : <RefreshCw size={17} />} Reintentar</button>
          <button className="secondary-button" type="button" onClick={onBack}>Empezar otra verificación</button>
        </div>
      </section>
    </main>
  );
}

function Citations({ citations, onSelect }) {
  if (!citations?.length) return null;
  return (
    <div className="legitimacy-citations">
      {citations.map((citation, index) => (
        <button type="button" onClick={() => onSelect(citation)} key={`${citation.fileName}-${citation.page}-${index}`} title={citation.quote}>
          {citation.fileName} · pág. {citation.page} <ChevronRight size={13} />
        </button>
      ))}
    </div>
  );
}

function LegitimacyResult({ api, check, onBack, onDelete }) {
  const assessment = check.assessment;
  const verdict = verdicts[assessment.verdict];
  const VerdictIcon = verdict.icon;
  const [sourceId, setSourceId] = useState(check.sources[0]?.id || "");
  const [page, setPage] = useState(1);
  const [copied, setCopied] = useState(false);
  const previewRef = useRef(null);
  const source = check.sources.find(({ id }) => id === sourceId) || check.sources[0];
  const previewApi = useMemo(() => ({
    getDocumentBlob: () => api.getLegitimacySourceBlob(check.id, source?.id),
  }), [api, check.id, source?.id]);
  const previewDocument = useMemo(() => source ? ({ ...source, demo: check.demo }) : null, [check.demo, source]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [check.id]);

  function selectCitation(citation) {
    const matching = check.sources.find(({ fileName }) => fileName === citation.fileName);
    if (matching) setSourceId(matching.id);
    setPage(citation.page || 1);
    window.requestAnimationFrame(() => previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  async function copySteps() {
    const content = [
      assessment.immediateAction.title,
      assessment.immediateAction.explanation,
      "",
      "Cómo comprobarlo:",
      ...assessment.verificationSteps.map((item, index) => `${index + 1}. ${item.title}: ${item.instruction}`),
      "",
      "Evitá:",
      ...assessment.avoidActions.map((item) => `- ${item}`),
      ...(assessment.recoveryPlan.needed ? ["", assessment.recoveryPlan.title, ...assessment.recoveryPlan.steps.map((item) => `- ${item}`)] : []),
    ].join("\n");
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <main className={`civita-document-page legitimacy-page legitimacy-result-page is-${assessment.verdict}`}>
      {check.demo && <div className="demo-banner"><Sparkles size={16} /> Este es un ejemplo ilustrativo. No se abrió el enlace ni se consultó al supuesto banco.</div>}
      <header className="result-header legitimacy-result-header">
        <button className="text-button" type="button" onClick={onBack}><ArrowLeft size={16} /> Mis verificaciones</button>
        <div className="result-header__actions">
          <button className="secondary-button" type="button" onClick={() => void copySteps()}>{copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Pasos copiados" : "Copiar pasos"}</button>
          {!check.demo && <button className="danger-icon-button" type="button" onClick={onDelete} title="Eliminar verificación"><Trash2 size={17} /></button>}
        </div>
      </header>

      <section className="legitimacy-result-heading">
        <div>
          <span className="eyebrow">Evaluación terminada</span>
          <h1>{assessment.title}</h1>
          <p>{assessment.summary}</p>
        </div>
        <aside className={`legitimacy-verdict is-${assessment.verdict}`}>
          <VerdictIcon size={24} />
          <div><small>{verdict.label}</small><strong>{verdict.short}</strong></div>
        </aside>
      </section>

      <section className={`legitimacy-immediate is-${assessment.verdict}`}>
        <span><ShieldAlert size={22} /></span>
        <div><small>Qué conviene hacer primero</small><h2>{assessment.immediateAction.title}</h2><p>{assessment.immediateAction.explanation}</p></div>
      </section>

      <section className="legitimacy-decision-message">
        <SearchCheck size={20} />
        <div><strong>En pocas palabras</strong><p>{assessment.decisionMessage}</p><small>Confianza del análisis: {assessment.confidence === "high" ? "alta" : assessment.confidence === "medium" ? "media" : "baja"}. {assessment.confidenceReason}</small></div>
      </section>

      <div className={`legitimacy-analysis-workspace ${check.sources.length ? "has-preview" : ""}`}>
        <div className="legitimacy-analysis-content">
          <section className="legitimacy-section">
            <header><span className="section-number">1</span><div><h2>Lo que realmente observamos</h2><p>Hechos del relato o de los archivos, sin completar lo que falta.</p></div></header>
            <div className="legitimacy-facts">
              {assessment.observedFacts.map((item, index) => (
                <article key={`${item.fact}-${index}`}><span>{item.origin === "attachment" ? <FileCheck2 size={16} /> : <MessageCircleQuestion size={16} />}</span><div><p>{item.fact}</p><small>{item.origin === "attachment" ? "Visto en un archivo" : "Contado por vos"}</small><Citations citations={item.citations} onSelect={selectCitation} /></div></article>
              ))}
            </div>
          </section>

          <section className="legitimacy-section">
            <header><span className="section-number">2</span><div><h2>Señales que encontramos</h2><p>Una señal aislada no prueba el origen; el conjunto ayuda a medir el riesgo.</p></div></header>
            <div className="legitimacy-signals">
              <section className="is-warning"><header><ShieldAlert size={18} /><h3>Señales de alerta</h3></header>{assessment.warningSigns.length ? assessment.warningSigns.map((item, index) => <article className={`is-${item.severity}`} key={`${item.title}-${index}`}><small>{item.severity === "high" ? "Importante" : item.severity === "medium" ? "A tener en cuenta" : "Señal leve"}</small><h4>{item.title}</h4><p>{item.explanation}</p><Citations citations={item.citations} onSelect={selectCitation} /></article>) : <p className="legitimacy-empty">No identificamos señales claras de alerta en lo aportado.</p>}</section>
              <section className="is-reassuring"><header><ShieldCheck size={18} /><h3>Datos que parecen coherentes</h3></header>{assessment.reassuringSigns.length ? assessment.reassuringSigns.map((item, index) => <article key={`${item.title}-${index}`}><h4>{item.title}</h4><p>{item.explanation}</p><small><b>Límite:</b> {item.limitation}</small><Citations citations={item.citations} onSelect={selectCitation} /></article>) : <p className="legitimacy-empty">No hay datos positivos que permitan reducir la duda. Aun cuando los hubiera, no confirmarían la identidad por sí solos.</p>}</section>
            </div>
          </section>

          {assessment.unverifiedClaims.length > 0 && (
            <section className="legitimacy-section legitimacy-unverified">
              <header><span className="section-number">3</span><div><h2>Lo que todavía no está comprobado</h2><p>Son afirmaciones recibidas, no hechos confirmados.</p></div></header>
              <div>{assessment.unverifiedClaims.map((item, index) => <article key={`${item.claim}-${index}`}><HelpCircle size={17} /><div><h3>{item.claim}</h3><p>{item.whyUnverified}</p><small><b>Cómo comprobarlo:</b> {item.howToVerify}</small></div></article>)}</div>
            </section>
          )}
        </div>

        {check.sources.length > 0 && (
          <div className="preview-column legitimacy-preview-column" ref={previewRef}>
            {check.sources.length > 1 && <label className="signing-source-picker"><span>Archivo que estás viendo</span><select value={source?.id || ""} onChange={(event) => { setSourceId(event.target.value); setPage(1); }}>{check.sources.map((item) => <option value={item.id} key={item.id}>{item.fileName}</option>)}</select></label>}
            <DocumentPreview api={previewApi} document={previewDocument} page={page} onPageChange={setPage} label="Evidencia aportada" demoContent={legitimacyDemoLines} />
            <p className="signing-preview-note"><LockKeyhole size={13} /> Leer el archivo no implica confirmar quién lo creó o envió.</p>
          </div>
        )}
      </div>

      <section className="legitimacy-verification-plan">
        <header><span className="section-icon"><ListChecks size={19} /></span><div><span className="eyebrow">Comprobación segura</span><h2>Cómo verificarlo sin exponerte</h2><p>Usá caminos independientes del mensaje o pedido que recibiste.</p></div></header>
        <ol>{assessment.verificationSteps.map((item, index) => <li className={`is-${item.priority}`} key={`${item.title}-${index}`}><span>{index + 1}</span><div><small>{priorityLabels[item.priority]}</small><h3>{item.title}</h3><p>{item.instruction}</p><em>{item.reason}</em></div></li>)}</ol>
      </section>

      <div className="legitimacy-protection-grid">
        <section className="legitimacy-avoid"><header><Ban size={19} /><div><span className="eyebrow">Mientras verificás</span><h2>Qué conviene evitar</h2></div></header><ul>{assessment.avoidActions.map((item) => <li key={item}><X size={15} /> {item}</li>)}</ul></section>
        <section className={`legitimacy-recovery ${assessment.recoveryPlan.needed ? "is-needed" : ""}`}><header>{assessment.recoveryPlan.needed ? <ShieldAlert size={19} /> : <CheckCircle2 size={19} />}<div><span className="eyebrow">Si ya actuaste</span><h2>{assessment.recoveryPlan.title}</h2></div></header>{assessment.recoveryPlan.steps.length ? <ol>{assessment.recoveryPlan.steps.map((item, index) => <li key={item}><span>{index + 1}</span>{item}</li>)}</ol> : <p>No informaste pagos, datos compartidos ni instalaciones que requieran una recuperación inmediata.</p>}</section>
      </div>

      {assessment.questionsForOfficialChannel.length > 0 && (
        <section className="legitimacy-official-questions"><Phone size={20} /><div><span className="eyebrow">Para un canal oficial independiente</span><h2>Preguntas que podés hacer</h2><ul>{assessment.questionsForOfficialChannel.map((item) => <li key={item}>{item}</li>)}</ul></div></section>
      )}

      <CivitaAssistantLauncher
        className="legitimacy-assistant"
        title="Seguí comprobando paso a paso"
        description="Abrí a Justiniano para explicar una señal u ordenar qué hacer sin contactar al remitente."
        sourceLabel="Solo esta verificación"
        conversationScope={`verificar-legitimidad:${check.id}:v1`}
        conversationMetadata={{ civitaLegitimacyCheckId: check.id, notebookId: check.notebookId }}
        notebookIds={check.sources.length ? [check.notebookId] : []}
        requestedSources={check.sources.length ? ["library"] : []}
        welcome="Ya organicé las señales y lo que todavía falta comprobar. Puedo ayudarte a seguir los pasos seguros o explicarte por qué algo genera dudas."
        suggestions={["¿Qué debería comprobar primero?", "Explicame la señal más importante", "Ya hice clic, ¿qué hago ahora?"]}
        appContext={{
          route: "/verificar-legitimidad",
          activeEntity: { type: "civita_legitimacy_check", id: check.id, label: assessment.title },
          legitimacyCheck: {
            category: check.category,
            description: check.description,
            claimedSender: check.claimedSender,
            receivedThrough: check.receivedThrough,
            requestedAction: check.requestedAction,
            detailsToCheck: check.detailsToCheck,
            concern: check.concern,
            pressure: check.pressure,
            alreadyActed: check.alreadyActed,
            assessment,
          },
        }}
      />

      <p className="ai-disclaimer">Esta evaluación fue generada con IA y puede equivocarse. Civita identifica señales y propone verificaciones; no certifica identidades, sitios, documentos ni remitentes.</p>
    </main>
  );
}

export function VerifyLegitimacyPage({ api, authEnabled }) {
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [claimedSender, setClaimedSender] = useState("");
  const [receivedThrough, setReceivedThrough] = useState("whatsapp");
  const [requestedAction, setRequestedAction] = useState("click_link");
  const [detailsToCheck, setDetailsToCheck] = useState("");
  const [concern, setConcern] = useState("");
  const [pressure, setPressure] = useState("none");
  const [alreadyActed, setAlreadyActed] = useState("no_action");
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [checks, setChecks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(authEnabled);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function selectCheck(check) {
    setSelected(check);
    if (!check?.demo) window.history.replaceState({}, "", `/verificar-legitimidad?verificacion=${encodeURIComponent(check.id)}`);
  }

  function resetView() {
    setSelected(null);
    setCategory("");
    setDescription("");
    setClaimedSender("");
    setReceivedThrough("whatsapp");
    setRequestedAction("click_link");
    setDetailsToCheck("");
    setConcern("");
    setPressure("none");
    setAlreadyActed("no_action");
    setFiles([]);
    setDeleteOpen(false);
    window.history.replaceState({}, "", "/verificar-legitimidad");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  useEffect(() => {
    if (!authEnabled) return undefined;
    let active = true;
    api.listLegitimacyChecks().then((items) => {
      if (!active) return;
      setChecks(items);
      const requestedId = new URLSearchParams(window.location.search).get("verificacion");
      const match = items.find(({ id }) => id === requestedId);
      if (match) setSelected(match);
    }).catch((error) => showToast({ message: error.message, tone: "danger" })).finally(() => active && setLoadingHistory(false));
    return () => { active = false; };
  }, [api, authEnabled, showToast]);

  useEffect(() => {
    if (!selected || selected.demo || ["ready", "failed"].includes(selected.status)) return undefined;
    let active = true;
    let timer;
    async function refresh() {
      try {
        let current = await api.getLegitimacyCheck(selected.id);
        const sourcesReady = current.sources?.every(({ status }) => status === "ready");
        if (sourcesReady && ["waiting", "analyzing"].includes(current.analysisStatus)) current = await api.analyzeLegitimacyCheck(current.id);
        if (!active) return;
        setSelected(current);
        setChecks((items) => items.map((item) => item.id === current.id ? current : item));
        if (!["ready", "failed"].includes(current.status)) timer = window.setTimeout(refresh, 1_800);
      } catch (error) {
        if (!active) return;
        if (error.code === "civita_legitimacy_check_not_ready") { timer = window.setTimeout(refresh, 1_800); return; }
        setSelected((current) => current ? { ...current, status: "failed", error: { message: error.message, code: error.code } } : current);
      }
    }
    void refresh();
    return () => { active = false; window.clearTimeout(timer); };
  }, [api, selected?.demo, selected?.id, selected?.status]);

  function chooseFiles(fileList) {
    const next = [...(fileList || [])];
    if (!next.length) return;
    if (next.length > 10) { showToast({ message: "Podés subir hasta 10 archivos.", tone: "danger" }); return; }
    const invalid = next.some((file) => !["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(file.type) && !/\.(?:pdf|jpe?g|png|webp)$/i.test(file.name));
    if (invalid) { showToast({ message: "Usá archivos PDF, JPG, PNG o WEBP.", tone: "danger" }); return; }
    if (next.reduce((total, file) => total + file.size, 0) > 20 * 1024 * 1024) { showToast({ message: "Los archivos superan el límite total de 20 MB.", tone: "danger" }); return; }
    setFiles(next);
  }

  async function submit(event) {
    event.preventDefault();
    if (!category || description.trim().length < 20 || submitting) return;
    if (!authEnabled) { selectCheck(demoCheck); return; }
    setSubmitting(true);
    try {
      const created = await api.createLegitimacyCheck({ category, description, claimedSender, receivedThrough, requestedAction, detailsToCheck, concern, pressure, alreadyActed, files });
      setChecks((items) => [created, ...items]);
      selectCheck(created);
    } catch (error) { showToast({ message: error.message, tone: "danger" }); }
    finally { setSubmitting(false); }
  }

  async function retry() {
    if (!selected || selected.demo || retrying) return;
    setRetrying(true);
    try {
      const current = await api.retryLegitimacyCheck(selected.id);
      setSelected(current);
      setChecks((items) => items.map((item) => item.id === current.id ? current : item));
    } catch (error) { showToast({ message: error.message, tone: "danger" }); }
    finally { setRetrying(false); }
  }

  async function remove() {
    if (!selected || selected.demo || deleting) return;
    setDeleting(true);
    try {
      await api.deleteLegitimacyCheck(selected.id);
      setChecks((items) => items.filter((item) => item.id !== selected.id));
      resetView();
      showToast({ message: "La verificación fue eliminada.", tone: "success" });
    } catch (error) { showToast({ message: error.message, tone: "danger" }); }
    finally { setDeleting(false); }
  }

  if (selected?.status === "ready" && selected.assessment) return (
    <><LegitimacyResult api={api} check={selected} onBack={resetView} onDelete={() => setDeleteOpen(true)} />{deleteOpen && <ConfirmDialog title="¿Eliminar esta verificación?" description="Se borrarán los archivos, la evaluación y las conversaciones asociadas. Esta acción no se puede deshacer." confirmLabel="Eliminar" busy={deleting} onClose={() => !deleting && setDeleteOpen(false)} onConfirm={remove} />}</>
  );
  if (selected?.status === "failed") return <LegitimacyError check={selected} retrying={retrying} onRetry={retry} onBack={resetView} />;
  if (selected) return <LegitimacyProcessing check={selected} onBack={resetView} />;

  return (
    <main className="civita-document-page legitimacy-page upload-page">
      <header className="upload-heading legitimacy-upload-heading">
        <span className="eyebrow">Verificar si es legítimo</span>
        <h1>Frená un momento.<br />Comprobalo sin exponerte.</h1>
        <p>Contanos qué recibiste antes de hacer clic, pagar o compartir información. Separamos hechos, señales de alerta y formas seguras de verificarlo.</p>
      </header>

      <div className="upload-layout legitimacy-upload-layout">
        <form className="upload-form legitimacy-form" onSubmit={submit}>
          <fieldset className="legitimacy-category-choice"><legend>¿Qué querés verificar?</legend><div>{categoryOptions.map(({ id, label, description: optionDescription, icon: Icon }) => <button className={category === id ? "is-selected" : ""} type="button" onClick={() => setCategory(id)} key={id}><Icon size={18} /><span><strong>{label}</strong><small>{optionDescription}</small></span></button>)}</div></fieldset>

          <label className="question-field"><span>¿Qué recibiste y qué pasó?</span><textarea value={description} onChange={(event) => setDescription(event.target.value.slice(0, 6_000))} placeholder="Copiá el mensaje o contalo con tus palabras. Incluí qué te dijeron y qué esperan que hagas." rows={5} required /><small className="field-help">No incluyas contraseñas, códigos de seguridad, claves ni números completos de tarjeta.</small></label>

          <div className="legitimacy-field-grid">
            <label><span>¿Quién dice enviarlo? <small>Opcional</small></span><input value={claimedSender} onChange={(event) => setClaimedSender(event.target.value.slice(0, 300))} placeholder="Por ejemplo: mi banco, una empresa, un familiar" /></label>
            <label><span>¿Por dónde te llegó?</span><select value={receivedThrough} onChange={(event) => setReceivedThrough(event.target.value)}>{channelOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
            <label><span>¿Qué te piden hacer?</span><select value={requestedAction} onChange={(event) => setRequestedAction(event.target.value)}>{actionOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
            <label><span>¿Ya hiciste algo?</span><select value={alreadyActed} onChange={(event) => setAlreadyActed(event.target.value)}>{actedOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
          </div>

          <label className="question-field"><span>Datos que querés comprobar <small>Opcional</small></span><textarea value={detailsToCheck} onChange={(event) => setDetailsToCheck(event.target.value.slice(0, 2_500))} placeholder="Pegá el enlace sin abrirlo, el correo, teléfono, alias, nombre o datos visibles del pedido." rows={3} /></label>
          <label className="question-field"><span>¿Qué es lo que te genera dudas? <small>Opcional</small></span><textarea value={concern} onChange={(event) => setConcern(event.target.value.slice(0, 1_500))} placeholder="Por ejemplo: me apuran, no conozco al remitente o el monto no coincide." rows={3} /></label>

          <fieldset className="legitimacy-pressure-choice"><legend>¿Te están apurando?</legend><div>{pressureOptions.map((option) => <button className={pressure === option.id ? "is-selected" : ""} type="button" onClick={() => setPressure(option.id)} key={option.id}><span>{option.id === "immediate" ? <ShieldAlert size={17} /> : option.id === "soon" ? <Clock3 size={17} /> : <CheckCircle2 size={17} />}</span><strong>{option.label}</strong><small>{option.description}</small></button>)}</div></fieldset>

          <section className={`legitimacy-upload-box ${dragging ? "is-dragging" : ""}`} onDragEnter={(event) => { event.preventDefault(); setDragging(true); }} onDragOver={(event) => event.preventDefault()} onDragLeave={() => setDragging(false)} onDrop={(event) => { event.preventDefault(); setDragging(false); chooseFiles(event.dataTransfer.files); }}>
            <input ref={fileInputRef} type="file" accept="application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png,image/webp,.webp" multiple onChange={(event) => chooseFiles(event.target.files)} />
            <header><span><UploadCloud size={20} /></span><div><strong>Adjuntá capturas o documentos <small>Opcional</small></strong><p>PDF, JPG, PNG o WEBP · hasta 10 archivos y 20 MB</p></div><button type="button" onClick={() => fileInputRef.current?.click()}>{files.length ? "Agregar otros" : "Elegir archivos"}</button></header>
            {files.length > 0 && <ul>{files.map((file) => <li key={`${file.name}-${file.size}`}><FileText size={14} /><span>{file.name}</span><small>{formatSize(file.size)}</small><button type="button" aria-label={`Quitar ${file.name}`} onClick={() => setFiles((items) => items.filter((item) => item !== file))}><X size={14} /></button></li>)}</ul>}
          </section>

          <button className="analyze-button" type="submit" disabled={!category || description.trim().length < 20 || submitting}>{submitting ? <LoaderCircle className="is-spinning" size={18} /> : <SearchCheck size={18} />}{submitting ? "Guardando información…" : "Verificar con cuidado"}{!submitting && <ArrowRight size={18} />}</button>
          {!authEnabled && <button className="example-button" type="button" onClick={() => selectCheck(demoCheck)}>Ver un ejemplo completo</button>}
          <p className="privacy-note"><LockKeyhole size={14} /> Tu información queda protegida en tu espacio personal. Civita no abre enlaces ni contacta al remitente.</p>
        </form>

        <aside className="how-it-works legitimacy-how-it-works">
          <span className="eyebrow">Qué vas a recibir</span>
          <ol>
            <li><span>1</span><div><strong>Señales separadas</strong><small>Qué observamos y qué sólo afirma el remitente.</small></div></li>
            <li><span>2</span><div><strong>Un nivel de riesgo claro</strong><small>Sin presentar una sospecha como una certeza.</small></div></li>
            <li><span>3</span><div><strong>Pasos independientes</strong><small>Cómo comprobarlo sin usar el mismo mensaje.</small></div></li>
            <li><span>4</span><div><strong>Ayuda si ya actuaste</strong><small>Qué priorizar si pagaste o compartiste datos.</small></div></li>
          </ol>
          <div className="scope-note"><BadgeCheck size={18} /><p><strong>Una apariencia convincente no alcanza.</strong> Un logo, un comprobante o un dato correcto pueden copiarse. Por eso verificamos por otra vía.</p></div>
        </aside>
      </div>

      {(loadingHistory || checks.length > 0) && (
        <section className="recent-documents"><header><div><span className="eyebrow">Tu historial</span><h2>Verificaciones recientes</h2></div></header>{loadingHistory ? <div className="history-loading"><LoaderCircle className="is-spinning" size={18} /> Cargando…</div> : <div className="document-list">{checks.map((check) => { const verdict = check.assessment ? verdicts[check.assessment.verdict] : null; return <button type="button" key={check.id} onClick={() => selectCheck(check)}><span className="document-list__icon"><SearchCheck size={19} /></span><span><strong>{check.assessment?.title || check.claimedSender || "Verificación en curso"}</strong><small>{verdict?.label || "Analizando"} · {formatDate(check.createdAt)}</small></span><span className={`document-status is-${check.status}`}>{check.status === "ready" ? "Lista" : check.status === "failed" ? "Revisar" : "Procesando"}</span><ChevronRight size={17} /></button>; })}</div>}</section>
      )}
    </main>
  );
}
